import type {
  ValidationCheckResult,
  ValidationIssue,
  SeverityLevel,
} from '../types';
import type {
  ValidationIssueFamilyCategory,
  ValidationIssueFamilySeverity,
  ValidationIssueFamilyTargetPage,
  ValidationQuickFillFocus,
} from '../../../shared/types/validationNavigation.types';
import {
  canValidationDisplayNumberBlockExport,
  compareValidationIdsByCriticality,
  EXPORT_READINESS_SUMMARY_VALIDATION_ID,
  normalizeValidationId,
  resolveValidationDefaultSeverity,
  resolveValidationIdCriticalityRank,
  isRemovedValidationFieldName,
  isRemovedValidationId,
} from '../validationConstants';
import { extractComponentName } from './familyGrouping';
import { resolveValidationRepairRoutingPolicy } from './validationRepairRoutingPolicy';
import { getActionableValidationIssues } from './validationEndTypeCompatibility';
import {
  buildActionParams,
  mergeActionQuickFillFocus,
} from './validationIssueRouting';
import type { ValidationIssueFamilyAction } from './validationIssueRouting';

export type {
  ValidationAdditionalCategorySection,
  ValidationConnectorSection,
  ValidationIssueActionParams,
  ValidationIssueFamilyCategory,
  ValidationIssueFamilyPrimaryActionType,
  ValidationIssueFamilySeverity,
  ValidationIssueFamilyTargetPage,
  ValidationMappingFocus,
  ValidationMappingNodeType,
  ValidationMappingReference,
  ValidationProcessSection,
  ValidationQuickFillAutofillMode,
  ValidationQuickFillFocus,
  ValidationQuickFillSuggestedCellValue,
} from '../../../shared/types/validationNavigation.types';

export type { ValidationIssueFamilyAction } from './validationIssueRouting';

export interface ValidationIssueFamilyExample {
  label: string;
  detail: string;
  elementIds: string[];
}

export interface ValidationFailingInstanceSummary {
  label: string;
  componentType: string;
  reason: string;
  elementIds: string[];
  occurrenceCount: number;
}

export interface ValidationFailingTypeSummary {
  componentType: string;
  instanceCount: number;
  issueCount: number;
  sampleInstances: string[];
}

export interface ValidationIssueFamily {
  id: string;
  category: ValidationIssueFamilyCategory;
  severity: ValidationIssueFamilySeverity;
  countStrategy?: CountStrategy;
  isBlocking: boolean;
  isRequiredOnly: boolean;
  title: string;
  summary: string;
  whyItMatters: string;
  recommendedFixPath: string;
  affectedCount: number;
  affectedItems: string[];
  impactedElementCount: number;
  impactedElementIds: string[];
  targetPage: ValidationIssueFamilyTargetPage;
  targetLabel: string;
  primaryAction: ValidationIssueFamilyAction;
  examples: ValidationIssueFamilyExample[];
  failingInstances: ValidationFailingInstanceSummary[];
  failingTypes: ValidationFailingTypeSummary[];
  validationIds: number[];
  checkTitles: string[];
  issueCount: number;
  rawIssues: ValidationIssue[];
}

export interface ValidationIssueFamilyBuildResult {
  families: ValidationIssueFamily[];
  passedChecks: ValidationCheckResult[];
}

type CountStrategy = 'fields' | 'elements' | 'issues';

interface FamilyDescriptor {
  groupKey: string;
  category: ValidationIssueFamilyCategory;
  blocksExport: boolean;
  requiredOnly: boolean;
  targetPage: ValidationIssueFamilyTargetPage;
  targetLabel: string;
  primaryAction: ValidationIssueFamilyAction;
  countStrategy: CountStrategy;
  title: (count: number) => string;
  summary: (count: number, items: string[], impactedElementCount: number) => string;
  whyItMatters: string;
  recommendedFixPath: string;
  affectedItems: string[];
}

interface FamilyAggregate extends Omit<ValidationIssueFamily, 'title' | 'summary' | 'affectedCount'> {
  descriptor: FamilyDescriptor;
  countTokens: Set<string>;
  affectedItemsSet: Set<string>;
  impactedElementIdsSet: Set<string>;
  validationIdsSet: Set<number>;
  checkTitlesSet: Set<string>;
  examplesMap: Map<string, ValidationIssueFamilyExample>;
}

const SEVERITY_RANK: Record<ValidationIssueFamilySeverity, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  info: 3,
};

const KNOWN_FIELD_PATTERN =
  /\b(OBJECT-TYPE|SKEY|PIPING-SPEC|END-TYPE(?:-\d+)?|ENDPOINT(?:-\d+)?|MATERIAL|PRESSURE-CLASS|DESIGN-STANDARD|INSULATION(?:-SPEC|THICKNESS)?|TRACING-SPEC|ITEM-DESCRIPTION|BOLT-(?:LENGTH|DIAMETER))\b/i;

const FIELD_NAME_ALIASES: Record<string, string> = {
  OBJECTTYPE: 'OBJECT-TYPE',
  PCFOBJECTTYPE: 'OBJECT-TYPE',
  SKEY: 'SKEY',
  PCFSKEY: 'SKEY',
  PIPINGSPEC: 'PIPING-SPEC',
  PIPPGSPEC: 'PIPING-SPEC',
  PIXPPGSPEC: 'PIPING-SPEC',
  ENDTYPE: 'END-TYPE-1',
  ENDTYPE1: 'END-TYPE-1',
  ENDTYPE2: 'END-TYPE-2',
  ENDTYPE3: 'END-TYPE-3',
  ENDTYPE4: 'END-TYPE-4',
  ENDPOINT1: 'END-POINT-1',
  ENDPOINT2: 'END-POINT-2',
  ENDPOINT3: 'END-POINT-3',
  ENDPOINT4: 'END-POINT-4',
  PIXPPGEND1TYPE: 'END-TYPE-1',
  PIXPPGEND2TYPE: 'END-TYPE-2',
  PIXPPGEND3TYPE: 'END-TYPE-3',
  PIXPPGEND4TYPE: 'END-TYPE-4',
  PIX03MATERIALENG: 'MATERIAL',
  MATERIALDESCRIPTION: 'MATERIAL',
  MATERIAL: 'MATERIAL',
  PRESSURECLASS: 'PRESSURE-CLASS',
  PIXPPGEND1PRESSURERATING: 'PRESSURE-CLASS',
  DESIGNSTANDARD: 'DESIGN-STANDARD',
  DESIGNSTD: 'DESIGN-STANDARD',
  PIX02STANDARDENG: 'DESIGN-STANDARD',
  INSULATIONSPEC: 'INSULATION-SPEC',
  PIXGN2INSULATIONSPEC: 'INSULATION-SPEC',
  INSULATIONTHICKNESS: 'INSULATION-THICKNESS',
  PIXGN2INSULATIONTHICKNESS: 'INSULATION-THICKNESS',
  TRACINGSPEC: 'TRACING-SPEC',
  PIXGN2HEATTRACINGSPEC: 'TRACING-SPEC',
  ITEMDESCRIPTION: 'ITEM-DESCRIPTION',
  PIX01DESCRIPTIONENG: 'ITEM-DESCRIPTION',
  BOLTLENGTH: 'BOLT-LENGTH',
  BOLTDIAMETER: 'BOLT-DIAMETER',
  BOLTDIA: 'BOLT-DIAMETER',
  PROCESSCONDITIONS: 'PROCESS-CONDITIONS',
  PROJECTPROPERTIES: 'PROJECT-PROPERTIES',
  PROJECTIDENTIFIER: 'PROJECT-IDENTIFIER',
  PROJECTNAME: 'PROJECT-NAME',
  AREA: 'AREA',
};

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function resolveIssueValidationIds(checkValidationId: number, issue: ValidationIssue): number[] {
  const validationIds = new Set<number>();
  const normalizedCheckValidationId = normalizeValidationId(checkValidationId);
  if (!isRemovedValidationId(normalizedCheckValidationId)) {
    validationIds.add(normalizedCheckValidationId);
  }

  const mergedFromValidationId = Number(issue.AdditionalData?.mergedFromValidationId);

  if (Number.isFinite(mergedFromValidationId) && !isRemovedValidationId(mergedFromValidationId)) {
    validationIds.add(normalizeValidationId(mergedFromValidationId));
  }

  return Array.from(validationIds);
}

function toFamilySeverity(
  validationId: number,
  fallbackSeverity: SeverityLevel,
  issue?: ValidationIssue
): ValidationIssueFamilySeverity {
  const usesMergedSystemNameSeverity =
    validationId === 32 || issue?.AdditionalData?.mergedFromValidationId === '32';
  const severity = usesMergedSystemNameSeverity
    ? fallbackSeverity
    : resolveValidationDefaultSeverity(validationId) ?? fallbackSeverity;
  switch (severity) {
    case 'Critical':
      return 'critical';
    case 'Error':
      return 'error';
    case 'Warning':
      return 'warning';
    default:
      return 'info';
  }
}

function maxSeverity(
  left: ValidationIssueFamilySeverity,
  right: ValidationIssueFamilySeverity
): ValidationIssueFamilySeverity {
  return SEVERITY_RANK[left] <= SEVERITY_RANK[right] ? left : right;
}

function canSeverityBlockExport(severity: ValidationIssueFamilySeverity): boolean {
  return severity === 'critical' || severity === 'error';
}

function resolveAggregatePrimaryValidationId(aggregate: Pick<FamilyAggregate, 'validationIdsSet'>): number | null {
  const validationIds = Array.from(aggregate.validationIdsSet.values());
  if (validationIds.length === 0) {
    return null;
  }

  return validationIds
    .map(normalizeValidationId)
    .sort(compareValidationIdsByCriticality)[0] ?? null;
}

function mergeAggregateInto(target: FamilyAggregate, source: FamilyAggregate): void {
  target.severity = maxSeverity(target.severity, source.severity);
  source.countTokens.forEach((token) => target.countTokens.add(token));
  source.affectedItemsSet.forEach((item) => target.affectedItemsSet.add(item));
  source.impactedElementIdsSet.forEach((id) => target.impactedElementIdsSet.add(id));
  source.validationIdsSet.forEach((validationId) => target.validationIdsSet.add(validationId));
  source.checkTitlesSet.forEach((title) => target.checkTitlesSet.add(title));
  target.rawIssues.push(...source.rawIssues);
  target.issueCount += source.issueCount;
  source.examplesMap.forEach((example, key) => {
    if (!target.examplesMap.has(key)) {
      target.examplesMap.set(key, example);
    }
  });
}

function normalizeFieldName(value: string | undefined): string | null {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/\s+/g, ' ').toUpperCase();
}

function resolveCanonicalFieldName(value: string | undefined): string | null {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return null;
  }

  const normalizedToken = trimmed.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!normalizedToken) {
    return null;
  }

  if (/^ATTRIBUTE([1-9]|1[0-9]|2[0-3])$/.test(normalizedToken)) {
    return normalizedToken;
  }

  return FIELD_NAME_ALIASES[normalizedToken] ?? null;
}

function extractContextLabels(issue: ValidationIssue): string[] {
  const componentName = (issue.AdditionalData?.componentName || '').trim();
  const systemName = (issue.AdditionalData?.systemName || '').trim();

  if (!componentName) {
    return [];
  }

  if (
    !systemName ||
    systemName.toLowerCase() === 'all systems' ||
    systemName.toLowerCase() === '(unassigned system)'
  ) {
    return [componentName];
  }

  return [`${componentName} · ${systemName}`];
}

function extractQuotedValues(message: string): string[] {
  const values: string[] = [];
  const singleQuotePattern = /'([^']+)'/g;
  const doubleQuotePattern = /"([^"]+)"/g;

  let match = singleQuotePattern.exec(message);
  while (match) {
    values.push(match[1]);
    match = singleQuotePattern.exec(message);
  }

  match = doubleQuotePattern.exec(message);
  while (match) {
    values.push(match[1]);
    match = doubleQuotePattern.exec(message);
  }

  return values;
}

function extractKnownFieldNameFromMessage(message: string): string | null {
  const match = message.match(KNOWN_FIELD_PATTERN);
  if (!match) {
    return null;
  }

  return resolveCanonicalFieldName(match[1]) ?? normalizeFieldName(match[1]);
}

function extractFieldNames(issue: ValidationIssue): string[] {
  const additionalDataFields = unique(
    [
      issue.AdditionalData?.pcfKey,
      issue.AdditionalData?.parameterName,
      issue.AdditionalData?.objectTypeParameterName,
      issue.AdditionalData?.skeyParameterName,
      issue.AdditionalData?.endType1ParameterName,
      issue.AdditionalData?.endType2ParameterName,
      issue.AdditionalData?.endType3ParameterName,
    ]
      .map(resolveCanonicalFieldName)
      .filter((value): value is string => Boolean(value))
      .filter((value) => !isRemovedValidationFieldName(value))
  );
  if (additionalDataFields.length > 0) {
    return additionalDataFields;
  }

  const messageFieldName = extractKnownFieldNameFromMessage(issue.Message);
  if (messageFieldName) {
    return [messageFieldName];
  }

  const quotedCandidates = extractQuotedValues(issue.Message)
    .map((value) => resolveCanonicalFieldName(value) ?? normalizeFieldName(value))
    .filter((value): value is string => Boolean(value))
    .filter((value) => !isRemovedValidationFieldName(value))
    .filter((value) => {
      if (value.includes('-')) {
        return true;
      }

      if (/^ATTRIBUTE([1-9]|1[0-9]|2[0-3])$/.test(value)) {
        return true;
      }

      if (KNOWN_FIELD_PATTERN.test(value)) {
        return true;
      }

      return false;
    });
  const candidates = quotedCandidates;

  if (candidates.length > 0) {
    return unique(candidates);
  }

  return [];
}

function extractElementLabels(issue: ValidationIssue): string[] {
  const labels =
    issue.AffectedElements.length > 0
      ? issue.AffectedElements.map((element) => extractComponentName(element))
      : issue.ElementIds.length > 0
        ? issue.ElementIds.map((id) => `Element ${id}`)
        : extractContextLabels(issue);

  return unique(labels.filter(Boolean));
}

function extractInstanceLabel(rawValue: string | undefined, elementId: string | undefined): string {
  const trimmed = (rawValue || '').trim();
  if (!trimmed) {
    return elementId ? `Element ${elementId}` : 'Unknown instance';
  }

  let label = trimmed;
  label = label.replace(/\s*\[\d+\]\s*/g, ' ');
  label = label.replace(/\s*\(ID:\s*\d+\)\s*/gi, ' ');
  label = label.replace(/\s{2,}/g, ' ').trim();

  return label || (elementId ? `Element ${elementId}` : 'Unknown instance');
}

function extractComponentTypeLabel(instanceLabel: string): string {
  const compact = instanceLabel.trim();
  if (!compact) {
    return 'Unknown type';
  }

  if (compact.includes(':')) {
    return compact.split(':')[0].trim() || compact;
  }

  if (compact.includes(' - ')) {
    return compact.split(' - ')[0].trim() || compact;
  }

  return compact;
}

function buildFailingInstanceSummaries(issues: ValidationIssue[]): ValidationFailingInstanceSummary[] {
  const instanceMap = new Map<
    string,
    {
      label: string;
      componentType: string;
      reasons: string[];
      reasonSet: Set<string>;
      elementIds: Set<string>;
      occurrenceCount: number;
    }
  >();

  for (const issue of issues) {
    const pairCount = Math.max(issue.AffectedElements.length, issue.ElementIds.length);
    for (let index = 0; index < pairCount; index += 1) {
      const elementId = issue.ElementIds[index];
      const rawLabel = issue.AffectedElements[index];
      const label = extractInstanceLabel(rawLabel, elementId);
      const componentType = extractComponentTypeLabel(label);
      const key = `${label}::${componentType}`;
      const existing = instanceMap.get(key);

      if (existing) {
        if (!existing.reasonSet.has(issue.Message)) {
          existing.reasonSet.add(issue.Message);
          existing.reasons.push(issue.Message);
        }
        if (elementId) {
          existing.elementIds.add(elementId);
        }
        existing.occurrenceCount += 1;
        continue;
      }

      instanceMap.set(key, {
        label,
        componentType,
        reasons: [issue.Message],
        reasonSet: new Set([issue.Message]),
        elementIds: new Set(elementId ? [elementId] : []),
        occurrenceCount: 1,
      });
    }
  }

  return Array.from(instanceMap.values())
    .map((entry) => ({
      label: entry.label,
      componentType: entry.componentType,
      reason: entry.reasons[0] || 'Validation issue detected',
      elementIds: Array.from(entry.elementIds).sort((left, right) => left.localeCompare(right)),
      occurrenceCount: entry.occurrenceCount,
    }))
    .sort((left, right) => {
      if (right.occurrenceCount !== left.occurrenceCount) {
        return right.occurrenceCount - left.occurrenceCount;
      }

      return left.label.localeCompare(right.label);
    });
}

function buildFailingTypeSummaries(
  instances: ValidationFailingInstanceSummary[]
): ValidationFailingTypeSummary[] {
  const typeMap = new Map<
    string,
    {
      componentType: string;
      instanceLabels: Set<string>;
      issueCount: number;
    }
  >();

  for (const instance of instances) {
    const existing = typeMap.get(instance.componentType);
    if (existing) {
      existing.instanceLabels.add(instance.label);
      existing.issueCount += instance.occurrenceCount;
      continue;
    }

    typeMap.set(instance.componentType, {
      componentType: instance.componentType,
      instanceLabels: new Set([instance.label]),
      issueCount: instance.occurrenceCount,
    });
  }

  return Array.from(typeMap.values())
    .map((entry) => ({
      componentType: entry.componentType,
      instanceCount: entry.instanceLabels.size,
      issueCount: entry.issueCount,
      sampleInstances: Array.from(entry.instanceLabels).sort((left, right) => left.localeCompare(right)).slice(0, 3),
    }))
    .sort((left, right) => {
      if (right.issueCount !== left.issueCount) {
        return right.issueCount - left.issueCount;
      }

      if (right.instanceCount !== left.instanceCount) {
        return right.instanceCount - left.instanceCount;
      }

      return left.componentType.localeCompare(right.componentType);
    });
}

function buildExample(issue: ValidationIssue, fallbackLabel: string): ValidationIssueFamilyExample {
  const fieldNames = extractFieldNames(issue);
  const elementLabels = extractElementLabels(issue);
  const contextLabels = extractContextLabels(issue);
  const label = fieldNames[0] || elementLabels[0] || contextLabels[0] || fallbackLabel;

  return {
    label,
    detail: issue.Message,
    elementIds: issue.ElementIds,
  };
}

function inferFallbackCategory(
  issue: ValidationIssue,
  check: ValidationCheckResult
): ValidationIssueFamilyCategory {
  const category = `${issue.Category || ''} ${check.Title || ''}`.toUpperCase();

  if (category.includes('CONNECT')) {
    return 'connectors';
  }

  if (category.includes('PARAMETER') || category.includes('EXPORT')) {
    return 'required-export';
  }

  if (category.includes('PROPERTY') || category.includes('DATA QUALITY') || category.includes('SUMMARY')) {
    return 'properties';
  }

  if (category.includes('COMPONENT') || category.includes('GEOMETRY')) {
    return 'components';
  }

  return 'components';
}

function buildModelReviewDescriptor(params: {
  groupKey: string;
  category: ValidationIssueFamilyCategory;
  title: (count: number) => string;
  summary: (count: number, items: string[], impactedElementCount: number) => string;
  whyItMatters: string;
  recommendedFixPath: string;
  affectedItems: string[];
  blocksExport?: boolean;
  countStrategy?: CountStrategy;
}): FamilyDescriptor {
  return {
    groupKey: params.groupKey,
    category: params.category,
    targetPage: 'validation',
    targetLabel: 'Validation',
    primaryAction: {
      label: 'Highlight in model',
      type: 'highlight',
    },
    countStrategy: params.countStrategy ?? 'issues',
    title: params.title,
    summary: params.summary,
    whyItMatters: params.whyItMatters,
    recommendedFixPath: params.recommendedFixPath,
    blocksExport: params.blocksExport ?? false,
    requiredOnly: false,
    affectedItems: params.affectedItems,
  };
}

function buildValidationReviewDescriptor(params: {
  groupKey: string;
  category: ValidationIssueFamilyCategory;
  title: (count: number) => string;
  summary: (count: number, items: string[], impactedElementCount: number) => string;
  whyItMatters: string;
  recommendedFixPath: string;
  affectedItems: string[];
  blocksExport?: boolean;
}): FamilyDescriptor {
  return {
    groupKey: params.groupKey,
    category: params.category,
    targetPage: 'validation',
    targetLabel: 'Validation',
    primaryAction: {
      label: 'Run validation again',
      route: 'pcfValidation',
      type: 'rerun',
    },
    countStrategy: 'issues',
    title: params.title,
    summary: params.summary,
    whyItMatters: params.whyItMatters,
    recommendedFixPath: params.recommendedFixPath,
    blocksExport: params.blocksExport ?? false,
    requiredOnly: false,
    affectedItems: params.affectedItems,
  };
}

function buildQuickFillDescriptor(params: {
  groupKey: string;
  category: ValidationIssueFamilyCategory;
  title: (count: number) => string;
  summary: (count: number, items: string[], impactedElementCount: number) => string;
  whyItMatters: string;
  recommendedFixPath: string;
  affectedItems: string[];
  countStrategy?: CountStrategy;
  blocksExport?: boolean;
  requiredOnly?: boolean;
  quickFillFocus?: ValidationQuickFillFocus;
}): FamilyDescriptor {
  return {
    groupKey: params.groupKey,
    category: params.category,
    targetPage: 'quick-fill',
    targetLabel: 'Quick Fill',
    primaryAction: {
      label: 'Open Quick Fill',
      route: 'quickFillProperties',
      type: 'navigate',
      ...(params.quickFillFocus ? { params: { quickFillFocus: params.quickFillFocus } } : {}),
    },
    countStrategy: params.countStrategy ?? 'issues',
    title: params.title,
    summary: params.summary,
    whyItMatters: params.whyItMatters,
    recommendedFixPath: params.recommendedFixPath,
    blocksExport: params.blocksExport ?? false,
    requiredOnly: params.requiredOnly ?? false,
    affectedItems: params.affectedItems,
  };
}

function buildDescriptor(issue: ValidationIssue, check: ValidationCheckResult): FamilyDescriptor {
  const routing = resolveValidationRepairRoutingPolicy(issue);
  const normalizedValidationId = routing.normalizedValidationId;
  const fieldNames = extractFieldNames(issue);
  const commonFieldHint = fieldNames.length > 0 ? fieldNames : extractElementLabels(issue);

  if (routing.reason === 'mapping-missing-model-field') {
    return {
      groupKey: 'mapping-missing-model-field',
      category: 'mapping',
      targetPage: 'mapping',
      targetLabel: 'Mapping',
      primaryAction: {
        label: 'Open Mapping',
        route: 'mappingConfiguration',
        type: 'navigate',
      },
      countStrategy: 'fields',
      title: (count) =>
        count === 1
          ? '1 mapped export field is missing in the model'
          : `${count} mapped export fields are missing in the model`,
      summary: (count, items) =>
        count === 1
          ? `One mapped export field points to a parameter that is not available in the current model.`
          : `Several mapped export fields point to parameters that are not available in the current model.`,
      whyItMatters:
        'The export cannot resolve these fields until the mapping points to parameters that exist in the model.',
      recommendedFixPath:
        'Open Mapping, review the affected export fields, and remap them to parameters that exist in the active model.',
      blocksExport: true,
      requiredOnly: true,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'mapping-value-coverage') {
    return {
      groupKey: 'required-export-no-model-values',
      category: 'required-export',
      targetPage: 'quick-fill',
      targetLabel: 'Quick Fill',
      primaryAction: {
        label: 'Open Quick Fill',
        route: 'quickFillProperties',
        type: 'navigate',
      },
      countStrategy: 'fields',
      title: (count) =>
        count === 1
          ? '1 mapped export field has no model values'
          : `${count} mapped export fields have no model values`,
      summary: () =>
        'The mapping exists, but the active model does not currently contain values for these export fields.',
      whyItMatters:
        'Mapped fields without values still leave the export incomplete, even when the mapping itself is valid.',
      recommendedFixPath:
        'Open Quick Fill, populate the missing values on the affected model items, and rerun validation.',
      blocksExport: true,
      requiredOnly: true,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'mapping-incomplete') {
    return {
      groupKey: 'mapping-incomplete-required',
      category: 'mapping',
      targetPage: 'mapping',
      targetLabel: 'Mapping',
      primaryAction: {
        label: 'Open Mapping',
        route: 'mappingConfiguration',
        type: 'navigate',
      },
      countStrategy: 'fields',
      title: (count) => `${count} required export mappings are incomplete`,
      summary: () =>
        'Required export fields still need a valid mapping before the export configuration is complete.',
      whyItMatters:
        'Required PCF fields must be mapped before downstream export and ISO workflows can resolve them reliably.',
      recommendedFixPath:
        'Open Mapping, complete the missing mappings, and run validation again.',
      blocksExport: true,
      requiredOnly: true,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'required-export-missing') {
    return {
      groupKey: 'required-export-missing',
      category: 'required-export',
      targetPage: 'quick-fill',
      targetLabel: 'Quick Fill',
      primaryAction: {
        label: 'Open Quick Fill',
        route: 'quickFillProperties',
        type: 'navigate',
      },
      countStrategy: 'elements',
      title: (count) => `${count} model items are missing required export data`,
      summary: (_count, items, impactedElementCount) => {
        const highlightedItems = items.slice(0, 3).join(', ');
        if (highlightedItems) {
          return `Required export data is missing across ${impactedElementCount} impacted items. Most common fields: ${highlightedItems}.`;
        }

        return 'Required export data is still missing on model items that need to participate in export.';
      },
      whyItMatters:
        'Missing export data leaves components unclassified for PCF output and slows down triage for downstream checks.',
      recommendedFixPath:
        'Open Quick Fill, populate the missing export values on the affected model items, and rerun validation.',
      blocksExport: true,
      requiredOnly: true,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'optional-skey') {
    return buildValidationReviewDescriptor({
      groupKey: 'optional-skey-etalon',
      category: 'properties',
      title: (count) => `${count} optional SKEY findings need review`,
      summary: () =>
        'The etalon allows this OBJECT-TYPE without a SKEY, so the finding stays in validation instead of creating a required export task.',
      whyItMatters:
        'Optional SKEY rows should not block export or send users to Quick Fill for values the etalon does not require.',
      recommendedFixPath:
        'Review the source validation report and rerun validation after the backend emits the updated etalon-aware result.',
      affectedItems: ['SKEY'],
      blocksExport: false,
    });
  }

  if (routing.reason === 'format-review') {
    return {
      groupKey: 'required-export-format',
      category: 'required-export',
      targetPage: 'quick-fill',
      targetLabel: 'Quick Fill',
      primaryAction: {
        label: 'Open Quick Fill',
        route: 'quickFillProperties',
        type: 'navigate',
      },
      countStrategy: 'issues',
      title: (count) => `${count} export classification values need review`,
      summary: (_count, items) =>
        items.length > 0
          ? `Some export classification values do not match the expected format. Review fields such as ${items.slice(0, 3).join(', ')}.`
          : 'Some export classification values do not match the expected format.',
      whyItMatters:
        'Incorrect classification values can still produce output, but the export is more likely to be rejected downstream or routed incorrectly.',
      recommendedFixPath:
        'Open Quick Fill, normalize the affected export values, and rerun validation.',
      blocksExport: true,
      requiredOnly: true,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'end-type-review') {
    if (normalizedValidationId === 11 || normalizedValidationId === 33) {
      return {
        groupKey: normalizedValidationId === 11 ? 'end-type-consistency' : 'end-type-etalon',
        category: 'required-export',
        targetPage: 'validation',
        targetLabel: 'Validation',
        primaryAction: {
          label: 'Open repair workspace',
          type: 'detail',
        },
        countStrategy: 'issues',
        title: (count) => `${count} END-TYPE values need review`,
        summary: () =>
          normalizedValidationId === 11
            ? 'END-TYPE assignments need case-by-case review. Safe End1/End2 slot swaps can be staged from this detail workspace when available; the rest stay manual review.'
            : 'END-TYPE/Object Type mismatches need case-by-case review so each failing item can be checked against the expected etalon rule before applying a fix.',
        whyItMatters:
          'END-TYPE values affect connector semantics in PCF output and mismatches can break downstream interpretation.',
        recommendedFixPath:
          normalizedValidationId === 11
            ? 'Open the repair workspace, stage safe End1/End2 slot swaps in Quick Fill when offered, review the remaining mismatches manually, and rerun validation.'
            : 'Open the repair workspace, review each END-TYPE mismatch case by case, apply the exact fix or auto-resolve step per issue, and rerun validation.',
        blocksExport: normalizedValidationId === 11,
        requiredOnly: normalizedValidationId === 11,
        affectedItems: ['END-TYPE-1', 'END-TYPE-2', 'END-TYPE-3'],
      };
    }

    return buildQuickFillDescriptor({
      groupKey: 'end-type-etalon',
      category: 'required-export',
      title: (count) => `${count} END-TYPE values need review`,
      summary: () =>
        'END-TYPE assignments do not line up with the expected connector roles and can be corrected from Quick Fill.',
      whyItMatters:
        'END-TYPE values affect connector semantics in PCF output and mismatches can break downstream interpretation.',
      recommendedFixPath:
        'Open Quick Fill, update the highlighted END-TYPE columns on the affected items, and rerun validation.',
      affectedItems: ['END-TYPE-1', 'END-TYPE-2', 'END-TYPE-3'],
      blocksExport: false,
      requiredOnly: false,
    });
  }

  if (routing.reason === 'parameter-review') {
    const descriptorMap: Record<number, Omit<FamilyDescriptor, 'targetPage' | 'targetLabel' | 'primaryAction'>> = {
      13: {
        groupKey: 'quick-fill-piping-spec',
        category: 'required-export',
        countStrategy: 'issues',
        title: (count) => `${count} PIPING-SPEC values need review`,
        summary: () =>
          'PIPING-SPEC values can be completed directly from Quick Fill for the affected model items.',
        whyItMatters:
          'Missing or incorrect PIPING-SPEC values block export readiness and downstream routing.',
        recommendedFixPath:
          'Open Quick Fill, review the highlighted PIPING-SPEC values, and rerun validation.',
        blocksExport: true,
        requiredOnly: true,
        affectedItems: ['PIPING-SPEC'],
      },
      17: {
        groupKey: 'quick-fill-insulation-spec',
        category: 'required-export',
        countStrategy: 'issues',
        title: (count) => `${count} insulation specifications need review`,
        summary: () =>
          'Insulation specification values can be corrected directly from Quick Fill for the affected model items.',
        whyItMatters:
          'Insulation metadata is exported alongside component data and placeholder values reduce output quality.',
        recommendedFixPath:
          'Open Quick Fill, review the highlighted INSULATION-SPEC values, and rerun validation.',
        blocksExport: false,
        requiredOnly: false,
        affectedItems: ['INSULATION-SPEC'],
      },
      18: {
        groupKey: 'quick-fill-insulation-thickness',
        category: 'required-export',
        countStrategy: 'issues',
        title: (count) => `${count} insulation thickness values need review`,
        summary: () =>
          'Insulation thickness values can be corrected directly from Quick Fill for the affected model items.',
        whyItMatters:
          'Invalid insulation thickness values create export noise and inconsistent downstream calculations.',
        recommendedFixPath:
          'Open Quick Fill, review the highlighted INSULATION-THICKNESS values, and rerun validation.',
        blocksExport: false,
        requiredOnly: false,
        affectedItems: ['INSULATION-THICKNESS'],
      },
      19: {
        groupKey: 'quick-fill-tracing-spec',
        category: 'required-export',
        countStrategy: 'issues',
        title: (count) => `${count} tracing specification values need review`,
        summary: () =>
          'Tracing specification values can be corrected directly from Quick Fill for the affected model items.',
        whyItMatters:
          'Tracing metadata affects PCF output quality and should be normalized before export.',
        recommendedFixPath:
          'Open Quick Fill, review the highlighted TRACING-SPEC values, and rerun validation.',
        blocksExport: false,
        requiredOnly: false,
        affectedItems: ['TRACING-SPEC'],
      },
    };

    return buildQuickFillDescriptor(descriptorMap[normalizedValidationId]);
  }

  if (routing.reason === 'connector-breaks') {
    return buildModelReviewDescriptor({
      groupKey: 'connectors-breaks',
      category: 'connectors',
      title: (count) => `${count} connector breaks need attention`,
      summary: () =>
        'Some connected elements have breaks or disconnections that should be reviewed directly in the model.',
      whyItMatters:
        'Broken or disconnected connector pairs can block export readiness and hide larger routing problems.',
      recommendedFixPath:
        'Highlight the impacted elements in the model, resolve the broken connections, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'connector-angle') {
    return buildModelReviewDescriptor({
      groupKey: 'connectors-angle',
      category: 'connectors',
      title: (count) => `${count} connector alignment checks need review`,
      summary: () =>
        'Connector alignment issues need a quick visual review in the model before export.',
      whyItMatters:
        'Alignment mismatches can create subtle model inconsistencies that are hard to spot later in the export workflow.',
      recommendedFixPath:
        'Highlight the affected elements, review the connector alignment in the model, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'orphaned-elements') {
    return buildModelReviewDescriptor({
      groupKey: 'orphaned-elements',
      category: 'components',
      title: (count) => `${count} orphaned piping elements need attention`,
      summary: () =>
        'Some pipe-related elements have connectors but no active connection and should be reviewed directly in the model.',
      whyItMatters:
        'Orphaned elements can create export noise, duplicate geometry, and hidden routing issues.',
      recommendedFixPath:
        'Highlight the affected elements in the model, reconnect or remove the orphaned items, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'connector-size') {
    return buildModelReviewDescriptor({
      groupKey: 'connectors-size',
      category: 'connectors',
      title: (count) => `${count} connector size checks need attention`,
      summary: () =>
        'Connected elements include size mismatches that should be reviewed before export.',
      whyItMatters:
        'Size mismatches can indicate invalid fittings or missing reducers that lead to export and constructability issues.',
      recommendedFixPath:
        'Highlight the affected elements, resolve the sizing mismatch in the model, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'system-assignment') {
    return buildModelReviewDescriptor({
      groupKey: normalizedValidationId === 8 ? 'system-assignment-error' : 'system-assignment-warning',
      category: 'components',
      title: (count) => `${count} system assignment issues need model review`,
      summary: () =>
        'Some components are not connected to a valid piping system and must be corrected directly in the model.',
      whyItMatters:
        'Unassigned systems prevent reliable grouping and export of affected piping elements.',
      recommendedFixPath:
        'Highlight the affected elements, connect them to the correct piping system, and rerun validation.',
      affectedItems: extractElementLabels(issue),
      blocksExport: normalizedValidationId === 8,
      countStrategy: 'elements',
    });
  }

  if (routing.reason === 'geometry-overlap') {
    return buildModelReviewDescriptor({
      groupKey: 'geometry-overlap',
      category: 'components',
      title: (count) => `${count} geometry overlaps need model review`,
      summary: () =>
        'Some elements overlap in space without an intended connection and should be inspected directly in the model.',
      whyItMatters:
        'Unresolved geometric overlaps can hide duplicate components, invalid clashes, and export ambiguity.',
      recommendedFixPath:
        'Highlight the overlapping elements, inspect whether the contact is intentional, resolve the overlap in-model, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'system-name') {
    return buildModelReviewDescriptor({
      groupKey: 'system-name-review',
      category: 'properties',
      title: (count) => `${count} system naming issues need review`,
      summary: () =>
        'Some elements are missing export-safe system names and should be inspected directly in the model.',
      whyItMatters:
        'System names control per-system export grouping and file naming during Export all.',
      recommendedFixPath:
        'Highlight the affected elements, correct the system assignment or rename the system to a file-safe value, and rerun validation.',
      affectedItems: extractElementLabels(issue),
    });
  }

  if (routing.reason === 'system-name-collision') {
    return buildValidationReviewDescriptor({
      groupKey: 'system-name-collision',
      category: 'properties',
      title: (count) => `${count} export file key collisions need review`,
      summary: () =>
        'Different system names currently sanitize to the same export file key and need validation-side review.',
      whyItMatters:
        'File-key collisions can overwrite or merge PCF outputs unexpectedly during Export all.',
      recommendedFixPath:
        'Review the conflicting system names, make the file keys unique, and rerun validation.',
      affectedItems: commonFieldHint,
    });
  }

  if (routing.reason === 'pipe-spec-connection') {
    return buildQuickFillDescriptor({
      groupKey: 'pipe-spec-connection-mismatch',
      category: 'required-export',
      title: (count) => `${count} PIPE-SPEC connection mismatches need review`,
      summary: () =>
        'Conflicting PIPE-SPEC values on connected elements can be aligned directly from Quick Fill.',
      whyItMatters:
        'PIPE-SPEC mismatches create inconsistent export data across connected model items.',
      recommendedFixPath:
        'Open Quick Fill, align the highlighted PIPING-SPEC values on the affected items, and rerun validation.',
      affectedItems: ['PIPING-SPEC'],
    });
  }

  if (routing.reason === 'object-type-category-match') {
    return buildQuickFillDescriptor({
      groupKey: 'object-type-category-match',
      category: 'components',
      title: (count) =>
        count === 1
          ? '1 OBJECT-TYPE category assignment needs review'
          : `${count} OBJECT-TYPE category assignments need review`,
      summary: () =>
        'OBJECT-TYPE values do not match their Revit categories and must be changed to category-compatible values.',
      whyItMatters:
        'Category-compatible OBJECT-TYPE values keep PCF component classification aligned with the export workflow and downstream interpretation.',
      recommendedFixPath:
        'Use Auto-detect OBJECT-TYPE or Open Quick Fill, choose OBJECT-TYPE values valid for each affected category, and rerun validation.',
      affectedItems: ['OBJECT-TYPE'],
      blocksExport: false,
      requiredOnly: false,
    });
  }

  if (['bolt-parameters', 'pipe-spec-system'].includes(routing.reason)) {
    return {
      groupKey: `properties-${normalizedValidationId}`,
      category: 'properties',
      targetPage: 'properties',
      targetLabel: normalizedValidationId === 20 ? 'Bolt connection' : 'Process properties',
      primaryAction: {
        label: 'Open properties',
        route:
          normalizedValidationId === 20
            ? 'boltConnection'
            : 'processProperties',
        type: 'navigate',
      },
      countStrategy: 'issues',
      title: (count) =>
        normalizedValidationId === 26
            ? `${count} system PIPE-SPEC consistency checks need review`
            : `${count} bolt parameter checks need attention`,
      summary: () =>
        normalizedValidationId === 26
            ? 'System-wide PIPE-SPEC consistency still needs the process-properties surface when the issue is not tied to exact model elements.'
            : 'Bolt-set configuration still needs the dedicated bolt settings surface.',
      whyItMatters:
        normalizedValidationId === 26
            ? 'System-level PIPE-SPEC consistency affects broad workflow output and may require rule or process-level decisions.'
            : 'Bolt-related values are configured from the dedicated bolt settings surface, not Quick Fill.',
      recommendedFixPath:
        normalizedValidationId === 26
            ? 'Open Process properties, review the system-wide PIPE-SPEC expectations, and run validation again.'
            : 'Open Bolt connection, align the affected bolt values, and run validation again.',
      blocksExport: false,
      requiredOnly: false,
      affectedItems: commonFieldHint,
    };
  }

  if (routing.reason === 'component-classification') {
    return {
      groupKey: 'components-classification',
      category: 'components',
      targetPage: 'quick-fill',
      targetLabel: 'Quick Fill',
      primaryAction: {
        label: 'Open Quick Fill',
        route: 'quickFillProperties',
        type: 'navigate',
      },
      countStrategy: 'issues',
      title: (count) => `${count} component classifications need review`,
      summary: () =>
        'Some component classifications do not align with the export workflow and need review.',
      whyItMatters:
        'Component classification drives object typing in PCF output and affects how downstream tools interpret the model.',
      recommendedFixPath:
        'Open Quick Fill, correct the affected classifications, and rerun validation.',
      blocksExport: false,
      requiredOnly: false,
      affectedItems: commonFieldHint,
    };
  }

  return {
    groupKey: `manual-review-${issue.ValidationId}`,
    category: inferFallbackCategory(issue, check),
    targetPage: 'validation',
    targetLabel: 'Validation',
    primaryAction: {
      label: 'Run validation again',
      route: 'pcfValidation',
      type: 'rerun',
    },
    countStrategy: 'issues',
    title: (count) =>
      count === 1
        ? '1 validation check needs manual review'
        : `${count} validation checks need manual review`,
    summary: () =>
      'This issue group does not map cleanly to a repair surface yet, so it stays in the validation workspace for manual triage.',
    whyItMatters:
      'Unreviewed validation issues increase the chance of hidden export failures and slower troubleshooting later.',
    recommendedFixPath:
      'Review the impacted items, resolve the underlying issue, and rerun validation.',
    blocksExport: issue.Severity === 'Critical' || issue.Severity === 'Error',
    requiredOnly: false,
    affectedItems: commonFieldHint,
  };
}

function buildCountTokens(
  descriptor: FamilyDescriptor,
  issue: ValidationIssue,
  fallbackLabel: string
): string[] {
  if (descriptor.countStrategy === 'fields') {
    return descriptor.affectedItems.length > 0
      ? descriptor.affectedItems
      : [fallbackLabel];
  }

  if (descriptor.countStrategy === 'elements') {
    if (descriptor.groupKey.startsWith('system-assignment-') && issue.ElementIds.length > 0) {
      return issue.ElementIds;
    }

    const elementLabels = extractElementLabels(issue);
    if (elementLabels.length > 0) {
      return elementLabels;
    }

    return issue.ElementIds.length > 0 ? issue.ElementIds : [fallbackLabel];
  }

  return [`${issue.ValidationId}:${issue.Message}:${issue.ElementIds.join(',')}`];
}

function isEffectivelyPassedCheck(check: ValidationCheckResult): boolean {
  if (check.Status === 'Passed') {
    return true;
  }

  const issues = check.Issues ?? [];
  return issues.length > 0 && getActionableValidationIssues(check).length === 0;
}

function buildPassedCheck(check: ValidationCheckResult): ValidationCheckResult {
  if (check.Status === 'Passed') {
    return check;
  }

  return {
    ...check,
    Status: 'Passed',
    Issues: [],
    IssueCount: 0,
    HasIssues: false,
  };
}

export function buildValidationIssueFamilies(
  checkResults: ValidationCheckResult[]
): ValidationIssueFamilyBuildResult {
  const aggregates = new Map<string, FamilyAggregate>();
  const passedChecks = checkResults
    .filter((check) =>
      normalizeValidationId(check.ValidationId) !== EXPORT_READINESS_SUMMARY_VALIDATION_ID &&
      !isRemovedValidationId(check.ValidationId) &&
      isEffectivelyPassedCheck(check)
    )
    .map(buildPassedCheck)
    .sort((left, right) => compareValidationIdsByCriticality(left.ValidationId, right.ValidationId));

  for (const check of checkResults) {
    if (
      normalizeValidationId(check.ValidationId) === EXPORT_READINESS_SUMMARY_VALIDATION_ID ||
      isRemovedValidationId(check.ValidationId)
    ) {
      continue;
    }

    if (check.Status === 'Passed' || !check.Issues || check.Issues.length === 0) {
      continue;
    }

    const actionableIssues = getActionableValidationIssues(check);
    if (actionableIssues.length === 0) {
      continue;
    }

    for (const issue of actionableIssues) {
      const descriptor = buildDescriptor(issue, check);
      const severity = toFamilySeverity(check.ValidationId, issue.Severity, issue);
      const normalizedValidationId = normalizeValidationId(check.ValidationId);
      const issueValidationIds = resolveIssueValidationIds(check.ValidationId, issue);
      const aggregateKey = [
        normalizedValidationId,
        descriptor.groupKey,
        descriptor.targetPage,
        descriptor.primaryAction.route || descriptor.primaryAction.type || 'none',
      ].join(':');
      const fallbackLabel = check.Title || issue.ValidationTitle || 'Validation issue';
      const countTokens = buildCountTokens(descriptor, issue, fallbackLabel);
      const example = buildExample(issue, fallbackLabel);

      const existing = aggregates.get(aggregateKey);
      if (existing) {
        existing.severity = maxSeverity(existing.severity, severity);
        existing.primaryAction = mergeActionQuickFillFocus(
          existing.primaryAction,
          descriptor.primaryAction
        );
        countTokens.forEach((token) => existing.countTokens.add(token));
        descriptor.affectedItems.forEach((item) => existing.affectedItemsSet.add(item));
        issue.ElementIds.forEach((id) => existing.impactedElementIdsSet.add(id));
        issueValidationIds.forEach((validationId) => existing.validationIdsSet.add(validationId));
        existing.checkTitlesSet.add(check.Title);
        existing.rawIssues.push(issue);
        existing.issueCount += 1;
        const exampleKey = `${example.label}:${example.detail}`;
        if (!existing.examplesMap.has(exampleKey)) {
          existing.examplesMap.set(exampleKey, example);
        }
        continue;
      }

      const aggregate: FamilyAggregate = {
        id: aggregateKey,
        category: descriptor.category,
        severity,
        isBlocking:
          descriptor.blocksExport &&
          canSeverityBlockExport(severity) &&
          canValidationDisplayNumberBlockExport(check.ValidationId),
        isRequiredOnly: descriptor.requiredOnly,
        whyItMatters: descriptor.whyItMatters,
        recommendedFixPath: descriptor.recommendedFixPath,
        affectedItems: [],
        impactedElementCount: 0,
        impactedElementIds: [],
        targetPage: descriptor.targetPage,
        targetLabel: descriptor.targetLabel,
        primaryAction: descriptor.primaryAction,
        examples: [],
        failingInstances: [],
        failingTypes: [],
        validationIds: [],
        checkTitles: [],
        issueCount: 1,
        rawIssues: [issue],
        descriptor,
        countTokens: new Set(countTokens),
        affectedItemsSet: new Set(descriptor.affectedItems),
        impactedElementIdsSet: new Set(issue.ElementIds),
        validationIdsSet: new Set(issueValidationIds),
        checkTitlesSet: new Set([check.Title]),
        examplesMap: new Map([[`${example.label}:${example.detail}`, example]]),
      };

      aggregates.set(aggregateKey, aggregate);
    }
  }

  const aggregateEntries = Array.from(aggregates.values());
  const manualReviewAggregates = aggregateEntries.filter((aggregate) =>
    aggregate.descriptor.groupKey.startsWith('manual-review-')
  );

  for (const manualReviewAggregate of manualReviewAggregates) {
    const primaryValidationId = resolveAggregatePrimaryValidationId(manualReviewAggregate);
    if (primaryValidationId == null) {
      continue;
    }

    const mergeCandidate = aggregateEntries.find((aggregate) => {
      if (aggregate === manualReviewAggregate) {
        return false;
      }

      if (aggregate.descriptor.groupKey.startsWith('manual-review-')) {
        return false;
      }

      return resolveAggregatePrimaryValidationId(aggregate) === primaryValidationId;
    });

    if (!mergeCandidate) {
      continue;
    }

    mergeAggregateInto(mergeCandidate, manualReviewAggregate);
    aggregates.delete(manualReviewAggregate.id);
  }

  const families = Array.from(aggregates.values())
    .map((aggregate) => {
      const affectedItems = Array.from(aggregate.affectedItemsSet);
      const impactedElementIds = Array.from(aggregate.impactedElementIdsSet).sort((left, right) =>
        left.localeCompare(right)
      );
      const failingInstances = buildFailingInstanceSummaries(aggregate.rawIssues);
      const failingTypes = buildFailingTypeSummaries(failingInstances);
      const affectedCount = aggregate.countTokens.size;

      return {
        id: aggregate.id,
        category: aggregate.category,
        severity: aggregate.severity,
        countStrategy: aggregate.descriptor.countStrategy,
        isBlocking:
          aggregate.descriptor.blocksExport &&
          canSeverityBlockExport(aggregate.severity) &&
          canValidationDisplayNumberBlockExport(Array.from(aggregate.validationIdsSet.values())),
        isRequiredOnly: aggregate.descriptor.requiredOnly,
        title: aggregate.descriptor.title(affectedCount),
        summary: aggregate.descriptor.summary(affectedCount, affectedItems, impactedElementIds.length),
        whyItMatters: aggregate.whyItMatters,
        recommendedFixPath: aggregate.recommendedFixPath,
        affectedCount,
        affectedItems,
        impactedElementCount: impactedElementIds.length,
        impactedElementIds,
        targetPage: aggregate.targetPage,
        targetLabel: aggregate.targetLabel,
        primaryAction: {
          ...aggregate.primaryAction,
          params: buildActionParams(aggregate.primaryAction, aggregate.targetPage, affectedItems),
        },
        examples: Array.from(aggregate.examplesMap.values()).slice(0, 6),
        failingInstances,
        failingTypes,
        validationIds: Array.from(aggregate.validationIdsSet.values()).sort(compareValidationIdsByCriticality),
        checkTitles: Array.from(aggregate.checkTitlesSet.values()),
        issueCount: aggregate.issueCount,
        rawIssues: aggregate.rawIssues,
      } satisfies ValidationIssueFamily;
    })
    .sort((left, right) => {
      const leftRank = Math.min(
        ...left.validationIds.map((validationId) => resolveValidationIdCriticalityRank(validationId))
      );
      const rightRank = Math.min(
        ...right.validationIds.map((validationId) => resolveValidationIdCriticalityRank(validationId))
      );
      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      const severityDifference = SEVERITY_RANK[left.severity] - SEVERITY_RANK[right.severity];
      if (severityDifference !== 0) {
        return severityDifference;
      }

      if (left.isBlocking !== right.isBlocking) {
        return left.isBlocking ? -1 : 1;
      }

      if (right.affectedCount !== left.affectedCount) {
        return right.affectedCount - left.affectedCount;
      }

      if (right.issueCount !== left.issueCount) {
        return right.issueCount - left.issueCount;
      }

      return left.title.localeCompare(right.title);
    });

  return {
    families,
    passedChecks,
  };
}
